<template>
    <div class="specialist-dashboard-insights">
        <div class="header-row">
        <div class="title">
            <h2>School Progress Insights</h2>
            <p>Key metrics and performance indicators</p>
        </div>
            <div class="tabs">
                <button 
                    :class="{ active: activeTab === 'screening' }"
                    @click="activeTab = 'screening'"
                    >Screening/Mini-Test</button>
                <button 
                    :class="{ active: activeTab === 'assessments' }"
                    @click="activeTab = 'assessments'"
                    >Assessments</button>
                <button 
                    :class="{ active: activeTab === 'interventions' }"
                    @click="activeTab = 'interventions'"
                    >Interventions</button>
            </div>
        </div>
        <router-link :to="{path: '/organizationDashboard/analytics', query: { tab: activeTab }}" class="graph-button">View Detailed Graphs</router-link>
        <div v-if="activeTab === 'screening'">
            <div class="stat-cards">
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="25" height="30" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"> <path d="M2 12h4l2-4 3 8 2-4h7" /></svg></div>
                            <h4>Screenings/Mini-Tests</h4>
                        </div>
                        <span class="trend">+16%</span>
                    </div>
                    <div class="stat-value">115</div>
                    <p class="subtext">Number of screenings delivered this year</p>
                    <p class="subtext small">vs. 96 last year</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg></div>
                            <div class="stacked-text">
                                <h4>Overall Progress</h4>
                                <span class="label">(Task Completion)</span>
                            </div>
                        </div>
                        <span class="trend">+8%</span>
                    </div>
                    <div class="stat-value">74%</div>
                    <p class="subtext">percentage of scheduled screenings completed</p>
                    <p class="subtext small">85 of 115 screenings</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="#D97706" stroke="none" /></svg></div>
                            <div class="stacked-text">
                                <h4>Score Trend</h4>
                                <span class="label">(Average Score)</span>
                            </div>
                        </div>
                        <span class="trend">+8%</span>
                    </div>
                    <div class="stat-value">85%</div>
                    <p class="subtext">Average performance across all screenings</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
                            <h4>Average Completion Time</h4>
                        </div>
                    </div>
                    <div class="stat-value">11 minutes</div>
                    <p class="subtext">Average duration per screening</p>
                </div>
            </div>
            <div class="key-insights-container">
                <h3>Key Insights</h3>
                <ul>
                    <li><strong>Screening/Mini-test effectiveness:</strong> Overall performance improved by 5% since last screening assessment period</li>
                    <li><strong>Growth trend:</strong> Number of screenings increased by 3 compared to 2 last period, showing improved 150% frequency</li>
                    <li><strong>Performance correlation:</strong> More engagement leading to higher scores</li> 
                </ul>
            </div>
        </div>
        <div v-else-if="activeTab === 'assessments'">
            <div class="stat-cards">
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="25" height="30" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"> <path d="M2 12h4l2-4 3 8 2-4h7" /></svg></div>
                            <h4>Assessments</h4>
                        </div>
                        <span class="trend">-17%</span>
                    </div>
                    <div class="stat-value">51</div>
                    <p class="subtext">Number of assessments delivered this year</p>
                    <p class="subtext small">vs. 60 last year</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg></div>
                            <div class="stacked-text">
                                <h4>Overall Progress</h4>
                                <span class="label">(Task Completion)</span>
                            </div>
                        </div>
                        <span class="trend">+8%</span>
                    </div>
                    <div class="stat-value">96%</div>
                    <p class="subtext">percentage of scheduled assessments completed</p>
                    <p class="subtext small">49 of 51 screenings</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="#D97706" stroke="none" /></svg></div>
                            <div class="stacked-text">
                                <h4>Score Trend</h4>
                                <span class="label">(Average Score)</span>
                            </div>
                        </div>
                        <span class="trend">+8%</span>
                    </div>
                    <div class="stat-value">85%</div>
                    <p class="subtext">Average performance across all assessments</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
                            <h4>Average Completion Time</h4>
                        </div>
                    </div>
                    <div class="stat-value">63 minutes</div>
                    <p class="subtext">Average duration per assessment</p>
                </div>
            </div>
            <div class="key-insights-container">
                <h3>Key Insights</h3>
                <ul>
                    <li><strong>Assessment effectiveness:</strong> Overall performance improved by 5% since last assessment period</li>
                    <li><strong>Growth trend:</strong> Number of assessments decreased by 2 compared to 3 last period, showing more focused exams</li>
                    <li><strong>Performance correlation:</strong> More engagement leading to higher scores</li> 
                </ul>
            </div>
        </div>
        <div v-else-if="activeTab === 'interventions'">
            <div class="stat-cards">
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="25" height="30" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"> <path d="M2 12h4l2-4 3 8 2-4h7" /></svg></div>
                            <h4>Interventions</h4>
                        </div>
                        <span class="trend">+25%</span>
                    </div>
                    <div class="stat-value">24</div>
                    <p class="subtext">Number of interventions delivered this year</p>
                    <p class="subtext small">vs. 19 last year</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg></div>
                            <div class="stacked-text">
                                <h4>Overall Progress</h4>
                                <span class="label">(Task Completion)</span>
                            </div>
                        </div>
                        <span class="trend">+8%</span>
                    </div>
                    <div class="stat-value">75%</div>
                    <p class="subtext">percentage of scheduled interventions completed</p>
                    <p class="subtext small">18 of 24 interventions</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="#D97706" stroke="none" /></svg></div>
                            <div class="stacked-text">
                                <h4>Score Trend</h4>
                                <span class="label">(Average Score)</span>
                            </div>
                        </div>
                        <span class="trend">+8%</span>
                    </div>
                    <div class="stat-value">85%</div>
                    <p class="subtext">Average performance across all interventions</p>
                </div>
                <div class="stat-card">
                    <div class="header-row-2">
                        <div class="header-left">
                            <div class="top-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9D5F0E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
                            <h4>Average Completion Time</h4>
                        </div>
                    </div>
                    <div class="stat-value">34 minutes</div>
                    <p class="subtext">Average duration per interventions</p>
                </div>
            </div>
            <div class="key-insights-container">
                <h3>Key Insights</h3>
                <ul>
                    <li><strong>Interventions effectiveness:</strong> Overall performance improved by 5% since last intervention period</li>
                    <li><strong>Growth trend:</strong> Number of interventions increased by 3 compared to 2 last period, showing improved 150% frequency</li>
                    <li><strong>Performance correlation:</strong> More engagement leading to higher scores</li> 
                </ul>
            </div>
        </div>
        </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'OrganizationDashboardInsights',
    data() {
        return {
            activeTab: 'screening',
            userLeveL: null,
        };
    },
    methods: {
        async establishRoleTableGuardian() {
            try {
                const baseUrl = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || "http://localhost:8081";

                // Get logged-in user ID
                const authResp = await fetch(`${baseUrl}/api/auth/get-access-cookie-data`, {
                credentials: "include",
                });
                if (!authResp.ok) throw new Error("Failed to fetch auth cookie data");

                const authData = await authResp.json();
                const ID = authData.id;

                // Check if guardian row exists
                let guardianExists = false;
                try {
                await axios.get(`${baseUrl}/api/guardian/user/${ID}`);
                guardianExists = true;
                } catch (err) {
                if (err.response && err.response.status === 404) {
                    guardianExists = false;
                } else {
                    throw err;
                }
                }

                if (guardianExists) {
                console.log("Guardian row already exists");
                return;
                }

                // Fetch user info
                const userResp = await axios.get(`${baseUrl}/api/user/${ID}`);
                const user = userResp.data;

                const [firstName, ...rest] = user.name.split(" . ");
                const lastName = rest.join(" ");

                const params = {
                    user_id: ID,
                    first_name: firstName,
                    last_name: lastName,
                    phone_number: user.phone_number,
                    email: user.email,
                };

                // Create guardian row
                await axios.post(`${baseUrl}/api/guardian`, params);

                console.log("Guardian row created");

            } catch (error) {
                console.error("Error establishing role table:", error);
            }
        },
        async establishRoleTableOrganization() {
            try {
                const baseUrl = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || "http://localhost:8081";

                // Get logged-in user ID
                const authResp = await fetch(`${baseUrl}/api/auth/get-access-cookie-data`, {
                credentials: "include",
                });
                if (!authResp.ok) throw new Error("Failed to fetch auth cookie data");

                const authData = await authResp.json();
                const ID = authData.id;

                // Check if organziation row exists
                let organziationExists = false;
                try {
                await axios.get(`${baseUrl}/api/organization/user/${ID}`);
                organziationExists = true;
                } catch (err) {
                if (err.response && err.response.status === 404) {
                    organziationExists = false;
                } else {
                    throw err;
                }
                }

                if (organziationExists) {
                console.log("Organization row already exists");
                return;
                }

                // Fetch user info
                const userResp = await axios.get(`${baseUrl}/api/user/${ID}`);
                const user = userResp.data;

                const orgName = user.name.replace(" . ", " ");

                const params = {
                    user_id: ID,
                    org_name: orgName,
                    department: user.department,
                    city: user.city,
                    country: user.country,
                    org_state: user.state,
                    zipcode: user.zipcode,
                    phone_number: user.phone_number,
                };

                // Create organization row
                await axios.post(`${baseUrl}/api/organization`, params);

                console.log("Organization row created");

            } catch (error) {
                console.error("Error establishing role table:", error);
            }
        },
        async establishRoleTableSpecialist() {
            try {
                const baseUrl = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || "http://localhost:8081";

                // Get logged-in user ID
                const authResp = await fetch(`${baseUrl}/api/auth/get-access-cookie-data`, {
                credentials: "include",
                });
                if (!authResp.ok) throw new Error("Failed to fetch auth cookie data");

                const authData = await authResp.json();
                const ID = authData.id;

                // Check if specialist row exists
                let specialistExists = false;
                try {
                await axios.get(`${baseUrl}/api/specialist/user/${ID}`);
                specialistExists = true;
                } catch (err) {
                if (err.response && err.response.status === 404) {
                    specialistExists = false;
                } else {
                    throw err;
                }
                }

                if (specialistExists) {
                console.log("Specialist row already exists");
                return;
                }

                // Fetch user info
                const userResp = await axios.get(`${baseUrl}/api/user/${ID}`);
                const user = userResp.data;

                const [firstName, ...rest] = user.name.split(" . ");
                const lastName = rest.join(" ");

                const params = {
                    user_id: ID,
                    first_name: firstName,
                    last_name: lastName,
                    phone_number: user.phone_number,
                    email: user.email,
                };

                // Create specialist row
                await axios.post(`${baseUrl}/api/specialist`, params);

                console.log("Specialist row created");

            } catch (error) {
                console.error("Error establishing role table:", error);
            }
        },
        async loadUserLevel() {
            const baseUrl = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || "http://localhost:8081";

            const resp = await fetch(`${baseUrl}/api/auth/get-access-cookie-data`, {
            credentials: "include",
            });

            if (!resp.ok) return;

            const data = await resp.json();
            this.userLeveL = data.role;
        }
    },
    async mounted() {
        await this.loadUserLevel();

        if (this.userLeveL === "Parent/Caretaker") {
            await this.establishRoleTableGuardian();
        }
        if(this.userLeveL === "Organization") {
            await this.establishRoleTableOrganization();
        }
        if(this.userLeveL === "Specialist"){
            await this.establishRoleTableSpecialist();
        }
    } 
};
</script>

<style scoped>
.specialist-dashboard-insights {
    padding: 40px;
    margin-top: 50px;
    background-color: white;
}
.header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.title{
    display: flex;
    flex-direction: column;
}
.title p{
    color: #444;
    margin-top: 4px;
    font-size: 18px;
    font-weight: 400;
}
.tabs {
  display: flex;
  gap: 10px;
  margin-top: 28px;
  border: 0.5px solid #ddd;
  border-radius: 8px;
  padding: 4px
}
.tabs button {
  padding: 9px 18px;
  cursor: pointer;
  font-size: 14px;
  transition: 0.2s;
  border-radius: 8px;
  margin-top: 4px;
  margin-bottom: 8px;
}
.tabs button:hover {
  background: #f2f2f2;
}
.tabs .active {
  color: #9D5f0E;
  background-color: #FFF5E3;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: underline;
}
.graph-button {
    display: inline-block;
    padding: 10px 16px;
    border: none;
    border-radius: 8px;

    font-size: 14px;
    font-weight: 600;

    color: white;
    text-decoration: none; 

    background: #9D5F0E;
    cursor: pointer;
    transition: 0.2s ease;
}
.graph-button:hover {
    background: #9D5F0E 10%;
}
.stat-cards {
  display: flex;
  gap: 20px;
  margin-top: 30px;
}
.stat-card {
  flex: 1;
  background: white;
  padding: 24px;
  border-radius: 14px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.header-row-2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.header-left{
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}
.top-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #FFEEDD; 
  display: flex;
  align-items: center;
  justify-content: center;
}
h4 {
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  color: #444
}
.label {
  font-size: 12px;
  color: #444;
  margin-top: -4px;
}
.stacked-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}
.trend {
  background: #E8FFE9;
  color: #0F8A27;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
}
.stat-value {
  font-size: 16px;
  margin-top: 10px;
  color: black
}
.subtext {
  color: #444;
  font-size: 13px;
  text-align: left;
}
.subtext.small {
  color: #444;
  font-size: 12px;
}
.key-insights-container {
    margin-top: 30px;
    background: #FFF5E3;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.next-up-cards {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-top: 30px;
}
.next-up-card {
    background: white;
    padding: 24px;
    border-radius: 14px;
    box-shadow: 0 4px 10px rgba(0,0,0,0.08);
    flex: 1 1 calc(25% - 20px);
    min-width: 240px;

    display: flex;
    flex-direction: column;
    gap: 10px;

    text-align: left;
}
.header-row-3 {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
}
.stacked-text h3 {
    margin: 0;
    font-size: 16px;
    color: #444;
    font-weight: 400;;
}
.stacked-text p {
    margin: 0;
    font-size: 16px;
    color: black;
}

.stacked-text p:last-child {
    font-size: 13px;
    color: #A16800;
}
</style>